import React, { useState, useEffect } from "react";
import { Box, Typography, Grid, TextField, Button, Rating, Snackbar, Alert } from "@mui/material";
import Slider from "react-slick";
import axios from "axios";
import { useTheme } from "@mui/material/styles";

const TestimonialComponent = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [rating, setRating] = useState(0);
  const [name, setName] = useState("");
  const [remark, setRemark] = useState("");

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Fetch testimonials from the backend
  const fetchTestimonials = async () => {
    try {
      const response = await axios.get("https://app.stock101.org/fetch-testimonials"); // Replace with your backend endpoint
      setTestimonials(response.data);
    } catch (error) {
      console.error("Error fetching testimonials:", error);
    }
  };

  // Handle testimonial submission
  const handleSubmit = async () => {
    if (!rating || !name || !remark) {
      setSnackbar({ open: true, message: "All fields are required!", severity: "warning" });
      return;
    }

    const newTestimonial = { rating, name, remark };

    try {
      await axios.post("https://app.stock101.org/add-testimonial", newTestimonial); // Replace with your backend endpoint
      setSnackbar({ open: true, message: "Thank you for your testimonial!", severity: "success" });
      setRating(0);
      setName("");
      setRemark("");

      // Fetch updated testimonials after submission
      fetchTestimonials();
    } catch (error) {
      console.error("Error submitting testimonial:", error);
      setSnackbar({ open: true, message: "An error occurred. Please try again later.", severity: "error" });
    }
  };

  // Close snackbar handler
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // React Slick settings
  const sliderSettings = {
    infinite: true,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    arrows: false,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  const theme = useTheme();

  // Fetch testimonials when the component loads
  useEffect(() => {
    fetchTestimonials();
  }, []);

  return (
    <Box
      sx={{
        fontFamily: "'Work Sans', sans-serif",
        padding: "20px 40px",
        textAlign: "center",
      }}
    >
{/* Carousel Section */}
<Box sx={{ marginBottom: "60px" }}>
  <Slider {...sliderSettings}>
    {testimonials.map((testimonial, index) => (
      <Box
        key={index}
        sx={{
          display: "inline-block",
          background: "linear-gradient(145deg, #f3f4f6, #e2e4e7)",
          borderRadius: "16px",
          padding: "20px",
          margin: "0 10px",
          boxShadow: "0 8px 16px rgba(0, 0, 0, 0.1)",
          textAlign: "center",
          maxWidth: "300px",
          transition: "transform 0.3s ease, box-shadow 0.3s ease",
          "&:hover": {
            transform: "scale(1.05)",
            boxShadow: "0 12px 24px rgba(0, 0, 0, 0.2)",
          },
          // Responsive styles for smaller screens
          [theme.breakpoints.down("sm")]: {
            maxWidth: "220px",
            padding: "15px",
          },
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: "bold",
            marginBottom: "10px",
            color: "#333",
            fontFamily: "'Work Sans', sans-serif",
            // Responsive font size
            fontSize: "1.5rem",
            [theme.breakpoints.down("sm")]: {
              fontSize: "1.2rem",
            },
          }}
        >
          {testimonial.name}
        </Typography>
        <Rating value={testimonial.rating} readOnly />
        <Typography
          variant="body1"
          sx={{
            color: "#666",
            marginTop: "10px",
            fontFamily: "'Work Sans', sans-serif",
            fontSize: "1rem",
            lineHeight: 1.5,
            // Responsive font size
            [theme.breakpoints.down("sm")]: {
              fontSize: "0.9rem",
            },
          }}
        >
          "{testimonial.remark}"
        </Typography>
      </Box>
    ))}
  </Slider>
</Box>

      {/* Submit Testimonial Section */}
      <Box
        sx={{
          maxWidth: "600px",
          margin: "0 auto",
          backgroundColor: "#fff",
          padding: "30px",
          borderRadius: "16px",
          boxShadow: "0 10px 20px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="body1" sx={{ marginBottom: "10px", fontWeight: "bold" }}>
              Your Rating:
            </Typography>
            <Rating
              value={rating}
              onChange={(event, newValue) => setRating(newValue)}
              sx={{ fontSize: "2rem" }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Your Name"
              variant="outlined"
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Your Remark"
              variant="outlined"
              multiline
              rows={4}
              fullWidth
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#5e60ce",
                color: "#fff",
                padding: "10px 20px",
                fontSize: "16px",
                fontWeight: "bold",
                "&:hover": {
                  backgroundColor: "#4e50be",
                },
              }}
              onClick={handleSubmit}
            >
              Submit Feedback
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Snackbar Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            width: "100%",
            fontFamily: "'Work Sans', sans-serif",
            fontSize: "16px",
            fontWeight: "bold",
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TestimonialComponent;
